/**
 * EventsRecapCard — Feb 2026
 *
 * Surfaces the most recent Event album on the Bulletin Board tab so
 * employees notice new galleries without having to swap tabs. Deliberately
 * warm & celebratory — soft gradient, sparkle icon, "New" chip — matches
 * the Events tab tone. If there are no recent albums, renders nothing.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, ChevronRight, Camera, Film } from "lucide-react";
import { mediaSrc } from "@/lib/authQuery";

export default function EventsRecapCard({ wsId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    if (!wsId) return;
    let alive = true;
    setBusy(true);
    api.get(`/workspaces/${wsId}/event-albums/recap?days=14&limit=3`)
      .then((r) => { if (alive) setRows(r.data?.rows || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [wsId]);

  if (busy || rows.length === 0) return null;

  const bg = "bg-gradient-to-r from-fuchsia-500/10 via-orange-400/10 to-amber-400/10 dark:from-fuchsia-500/15 dark:via-orange-400/15 dark:to-amber-400/15";

  return (
    <section
      className={`relative overflow-hidden border border-fuchsia-500/30 ${bg}`}
      data-testid="bulletin-recap-card"
    >
      {/* Sparkle rings — pure CSS accents */}
      <div className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full bg-fuchsia-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl" />

      <div className="relative px-4 sm:px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="label-eyebrow text-fuchsia-700 dark:text-fuchsia-300">Fresh from HR</div>
            <div className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>
              Recent moments · {rows.length} album{rows.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        <div className="flex-1 flex gap-2 overflow-x-auto pb-1 sm:pb-0" data-testid="bulletin-recap-tiles">
          {rows.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={onOpen}
              className="group relative shrink-0 w-40 sm:w-48 aspect-video border border-white/40 dark:border-white/10 overflow-hidden hover:scale-[1.02] transition-transform"
              data-testid={`bulletin-recap-tile-${a.id}`}
              title={a.title}
            >
              {a.cover ? (
                <img
                  src={mediaSrc(a.cover.url)}
                  alt={a.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-orange-500" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute inset-x-2 bottom-1 text-white text-[11px] font-semibold line-clamp-2 leading-tight">{a.title}</div>
              <div className="absolute top-1 left-1 text-[9px] font-bold uppercase tracking-widest text-white bg-fuchsia-500 px-1.5 py-0.5">New</div>
              <div className="absolute top-1 right-1 flex items-center gap-1 text-[9px] font-semibold text-white bg-black/50 px-1.5 py-0.5">
                <Camera className="w-2.5 h-2.5" /> {a.photo_count}
                {a.video_count > 0 && <><Film className="w-2.5 h-2.5 ml-1" /> {a.video_count}</>}
              </div>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="shrink-0 self-stretch sm:self-auto px-3 py-2 border border-fuchsia-500/40 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-500 hover:text-white transition-colors text-sm font-semibold flex items-center gap-1"
          data-testid="bulletin-recap-open"
        >
          View all <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
